import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { CalendarClock, Plus, Send, Siren } from 'lucide-react';
import {
  Button,
  DataTable,
  DateField,
  EmptyState,
  ErrorState,
  FormField,
  PageHeader,
  SearchPanel,
  Select,
  StatusBadge,
  TextField,
  type Column,
} from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { hasRole } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect } from '@/features/masters';
import { PAGE_SIZE, useNotices, type NoticeListFilter, type NoticeListItem } from './api';
import { FixedReportDatePicker } from './components/FixedReportDatePicker';
import './advance-notices.css';

const KEYS = ['businessOperatorId', 'vesselName', 'registrationNo', 'portName', 'registrationType', 'departureFrom', 'departureTo', 'arrivalFrom', 'arrivalTo', 'page'] as const;

function useFilterFromUrl(): [NoticeListFilter, (f: NoticeListFilter) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<NoticeListFilter>(() => {
    const f: Record<string, string | number> = {};
    KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) f[k] = k === 'businessOperatorId' || k === 'page' ? Number(v) : v;
    });
    return f as NoticeListFilter;
  }, [params]);
  const setFilter = (f: NoticeListFilter) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v !== '' && v != null && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, setFilter];
}

export const REG_TONE = { 新規登録: 'info', 変更: 'warning', 中止: 'danger' } as const;
export const vesselLabel = (r: { vesselName: string | null; unlistedVesselName: string | null }) => r.vesselName ?? r.unlistedVesselName ?? '';
const fmtAt = (s: string) => s.slice(5).replace('-', '/');

/** S-20 事前連絡一覧（登録・編集画面）。実施船単位の一覧から定時連絡・速報を登録する */
export function AdvanceNoticeListPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const canEdit = hasRole(user, ['基金', '漁協']);
  const { year, yearPath } = useFiscalYear();
  const [filter, setFilter] = useFilterFromUrl();
  const { data, isPending, isFetching, error, refetch } = useNotices(year, filter);
  const { register, handleSubmit, reset, control } = useForm<NoticeListFilter>({ values: filter });
  const [pickRow, setPickRow] = useState<number | null>(null);

  const reportable = (r: NoticeListItem) => r.vesselId != null && r.registrationType !== '中止' && r.changeType !== '削除';

  const columns: Column<NoticeListItem>[] = [
    {
      key: 'noticeNo',
      header: '連絡書番号',
      width: 130,
      render: (r) => (
        <>
          <span className="bs-num">{r.noticeNo}</span>
          <span className="bs-cell-sub">
            <StatusBadge tone={REG_TONE[r.registrationType]}>{r.registrationType}</StatusBadge>
          </span>
        </>
      ),
    },
    { key: 'businessOperatorName', header: '事業実施者', width: 170 },
    {
      key: 'vesselName',
      header: '実施船名',
      nowrap: true,
      render: (r) => (
        <>
          <span className={r.vesselId == null ? 'bs-unlisted' : undefined}>{vesselLabel(r)}</span>
          <span className="bs-cell-sub bs-num">{r.registrationNo ?? '名簿外'}</span>
        </>
      ),
    },
    { key: 'portName', header: '港名', width: 90 },
    {
      key: 'departureAt',
      header: '出港 → 入港',
      width: 190,
      render: (r) => (
        <span className="bs-num">
          {fmtAt(r.departureAt)} → {fmtAt(r.arrivalAt)}
        </span>
      ),
    },
    { key: 'crewCount', header: '乗組員', numeric: true, width: 70, render: (r) => `${r.crewCount}人` },
    { key: 'zoneNumbers', header: '農林漁区番号', width: 110, render: (r) => <span className="bs-num">{r.zoneNumbers}</span> },
    {
      key: 'fixedReportCount',
      header: '定時',
      width: 64,
      align: 'center',
      render: (r) => (r.fixedReportCount ? <StatusBadge tone="success">{r.fixedReportCount}件</StatusBadge> : <span className="bs-cell-sub">—</span>),
    },
    {
      key: 'voyageReportId',
      header: '速報',
      width: 64,
      align: 'center',
      render: (r) => (r.voyageReportId ? <StatusBadge tone="success">済</StatusBadge> : <StatusBadge tone="warning">未</StatusBadge>),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            width: 210,
            render: (r: NoticeListItem) =>
              reportable(r) ? (
                <div className="bs-row-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <Button size="sm" icon={<CalendarClock size={14} />} onClick={() => setPickRow(r.noticeVesselId)}>
                    定時連絡登録
                  </Button>
                  <Button
                    size="sm"
                    variant="accent"
                    icon={<Siren size={14} />}
                    onClick={() =>
                      navigate(r.voyageReportId ? yearPath(`voyage-reports/${r.voyageReportId}/edit`) : yearPath(`voyage-reports/new?noticeVesselId=${r.noticeVesselId}`))
                    }
                  >
                    速報登録
                  </Button>
                </div>
              ) : null,
          } satisfies Column<NoticeListItem>,
        ]
      : []),
  ];

  const summary = [filter.vesselName && `船名: ${filter.vesselName}`, filter.portName && `港: ${filter.portName}`, filter.registrationType, filter.departureFrom && `出港 ${filter.departureFrom}〜`]
    .filter(Boolean)
    .join(' ／ ');

  return (
    <>
      <PageHeader
        eyebrow="日々の報告"
        title="事前連絡"
        description="作業実施船の出港予定（別記様式第4号）を登録します。各行の「定時連絡登録」「速報登録」から定時連絡・速報を作成できます。"
        actions={
          canEdit && (
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => navigate(yearPath('advance-notices/new'))}>
              新規登録
            </Button>
          )
        }
      />
      <SearchPanel
        onSubmit={handleSubmit((v) => setFilter({ ...v, page: 1 }))}
        onReset={() => {
          reset({});
          setFilter({});
        }}
        summary={summary || '条件なし'}
        searching={isFetching}
        columns={5}
      >
        <FormField label="事業実施者">
          <Controller
            control={control}
            name="businessOperatorId"
            render={({ field }) => (
              <BusinessOperatorSelect fiscalYear={year} value={field.value ? Number(field.value) : null} onChange={(v) => field.onChange(v ?? '')} width="full" />
            )}
          />
        </FormField>
        <FormField label="船名" hint="船名・カナ・選択肢に無い船名の部分一致">
          <TextField {...register('vesselName')} />
        </FormField>
        <FormField label="漁船登録番号">
          <TextField {...register('registrationNo')} />
        </FormField>
        <FormField label="港名">
          <TextField {...register('portName')} />
        </FormField>
        <FormField label="登録区分">
          <Select placeholder="すべて" options={['新規登録', '変更', '中止'].map((v) => ({ value: v, label: v }))} {...register('registrationType')} />
        </FormField>
        <FormField label="出港日（から）">
          <DateField {...register('departureFrom')} />
        </FormField>
        <FormField label="出港日（まで）">
          <DateField {...register('departureTo')} />
        </FormField>
        <FormField label="入港日（から）">
          <DateField {...register('arrivalFrom')} />
        </FormField>
        <FormField label="入港日（まで）">
          <DateField {...register('arrivalTo')} />
        </FormField>
      </SearchPanel>
      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="事前連絡（実施船）一覧"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => r.noticeVesselId}
          loading={isPending}
          pagination={{ pageSize: PAGE_SIZE, page: filter.page ?? 1, total: data?.total ?? 0, onPageChange: (p) => setFilter({ ...filter, page: p }) }}
          onRowClick={(r) => navigate(yearPath(`advance-notices/${r.noticeId}`))}
          rowClassName={(r) => (r.changeType === '削除' || r.registrationType === '中止' ? 'is-muted' : undefined)}
          empty={<EmptyState compact icon={<Send />} title="事前連絡はまだありません" description={canEdit ? '「新規登録」から出港予定を登録してください。' : '検索条件を変更してください。'} />}
        />
      )}
      <FixedReportDatePicker rowId={pickRow} onClose={() => setPickRow(null)} />
    </>
  );
}
